import { Link } from '@tanstack/react-router';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { DrilldownLink } from '../drilldowns';

/**
 * Generic tabular tile body — header row + N body rows. Used for tiles
 * that need multiple comparable measures per row (e.g. labels with
 * separate open/closed counts), where a bar-chart row would compress
 * the second measure into a tiny annotation. The dashboard tile body
 * is small, so column count should stay ≤ 4 and labels concise.
 *
 * Columns are declared as `{ key, header, render, getDrilldown }` —
 * `render` returns the cell content; if `getDrilldown` is set the cell
 * value is wrapped in a `<Link>` to the configured destination.
 */
export interface TableColumn<T> {
  key: string;
  header: string;
  /** Right-align numeric columns. Defaults to `left`. */
  align?: 'left' | 'right';
  render: (row: T) => React.ReactNode;
  /**
   * Per-cell drilldown builder. When set, the cell content is rendered
   * inside a `<Link>` with the returned destination + search params.
   */
  getDrilldown?: (row: T) => DrilldownLink | undefined;
}

export function TableTile<T>({
  columns,
  rows,
  isLoading,
  isError,
  emptyMessage = 'No data',
  rowKey,
}: {
  columns: TableColumn<T>[];
  rows: T[];
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
  rowKey: (row: T) => string;
}): React.ReactElement {
  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Failed to load
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((c) => (
            <TableHead key={c.key} className={c.align === 'right' ? 'text-right' : undefined}>
              {c.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={rowKey(row)}>
            {columns.map((c) => {
              const cellClass = c.align === 'right' ? 'text-right tabular-nums' : 'truncate';
              const drilldown = c.getDrilldown?.(row);
              const content = c.render(row);
              return (
                <TableCell key={c.key} className={cellClass}>
                  {drilldown ? (
                    <Link
                      to={drilldown.to}
                      search={drilldown.search}
                      title={drilldown.title}
                      className={cn(
                        'inline-block rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                        c.align === 'left' && 'max-w-full truncate align-middle'
                      )}
                    >
                      {content}
                    </Link>
                  ) : (
                    content
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
